import jsPDF from "jspdf";
import API from "../services/api/axios";
import { getBothCourtCasesDetailsByCourtRef } from "../services/api/CasesApi";
import { getUserData } from "../services/api/AuthApi";
import { getBranchByBranchID } from "../services/api/BranchApi";
import { highCourtMap } from "../constants/CourtNames";

export const fetchUserName = async (userId) => {
    try {
        const res = await getUserData(userId);
        return res.data?.name || res.data?.username || 'Unknown User';
    } catch (err) {
        console.error('Error fetching user data:', err);
        return 'Unknown User';
    }
};

export const fetchBranchName = async (branchId) => {
    try {
        const response = await getBranchByBranchID(branchId);
        return response.data?.name || response.data?.branchName || 'Unknown Branch';
    } catch (error) {
        console.error('Error fetching branch name:', error);
        return 'Unknown Branch';
    }
};

// Helper function to get high court name by ID
const getHighCourtName = (courtId) => {
    const id = parseInt(courtId);
    return highCourtMap[id] || highCourtMap[courtId] || 'Unknown Court';
};

export const exportDashboardToPDF = async (dashboardData, orientation = 'portrait') => {
    console.log('Starting PDF export process');
    console.log('Exporting dashboard data to PDF:', dashboardData);
    console.log('Orientation:', orientation);

    const doc = new jsPDF({
        orientation: orientation,
        unit: 'pt',
        format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    let currentY = margin;
    let pageNumber = 1;

    // Helper functions
    const formatDate = (dateString) => {
        if (!dateString) return 'Not scheduled';
        try {
            let date;
            
            if (dateString.includes('th ') || dateString.includes('st ') || dateString.includes('nd ') || dateString.includes('rd ')) {
                let cleanDate = dateString.replace(/(\d+)(th|st|nd|rd)/, '$1');
                date = new Date(cleanDate);
            } else if (dateString.includes('T')) {
                date = new Date(dateString);
            } else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) return 'TBD';
            
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Date formatting error:', error, dateString);
            return 'TBD';
        }
    };

    const cleanText = (text, fallback = 'Not specified') => {
        if (text === null || text === undefined || text === '') return fallback;
        const cleanedText = String(text).trim();
        return cleanedText.length > 0 ? cleanedText : fallback;
    };

    const wrapTextInCell = (text, maxWidth, fontSize = 10, maxLines = 4) => {
        const textStr = cleanText(text, '');
        doc.setFontSize(fontSize);
        
        const words = textStr.split(' ');
        const lines = [];
        let currentLine = '';

        for (let i = 0; i < words.length; i++) {
            const testLine = currentLine + (currentLine ? ' ' : '') + words[i];
            const testWidth = doc.getStringUnitWidth(testLine) * fontSize / doc.internal.scaleFactor;

            if (testWidth > maxWidth && currentLine) {
                lines.push(currentLine);
                currentLine = words[i];
                if (lines.length >= maxLines) break;
            } else {
                currentLine = testLine;
            }
        }

        if (currentLine && lines.length < maxLines) {
            lines.push(currentLine);
        }

        return { 
            lines: lines.length > 0 ? lines : [''], 
            height: lines.length * fontSize * 1.4 
        };
    };

    const checkNewPage = (requiredSpace = 100) => {
        if (currentY + requiredSpace > pageHeight - 100) {
            addFooter();
            doc.addPage();
            pageNumber++;
            currentY = margin;
            addHeader();
            return true;
        }
        return false;
    };

    const addFooter = () => {
        // Get company name from localStorage
        const getCompanyName = () => {
            try {
                // Try different possible localStorage keys for company data
                const globalSettings = localStorage.getItem('globalSettings');
                const company = localStorage.getItem('company');
                const userData = localStorage.getItem('userData');
                const userProfile = localStorage.getItem('userProfile');
                
                if (globalSettings) {
                    const settings = JSON.parse(globalSettings);
                    if (settings.companyName) return settings.companyName;
                }
                
                if (company) {
                    const companyData = JSON.parse(company);
                    if (companyData.companyName) return companyData.companyName;
                    if (companyData.name) return companyData.name;
                }
                
                if (userData) {
                    const user = JSON.parse(userData);
                    if (user.companyName) return user.companyName;
                    if (user.company && user.company.name) return user.company.name;
                }
                
                if (userProfile) {
                    const profile = JSON.parse(userProfile);
                    if (profile.companyName) return profile.companyName;
                    if (profile.company) return profile.company;
                }
                
                return 'LAW FIRM';
            } catch (error) {
                console.error('Error retrieving company name:', error);
                return 'LAW FIRM';
            }
        };

        const companyName = getCompanyName();
        
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        
        const footerY = pageHeight - 30;
        doc.text(companyName, margin, footerY);
        doc.text(`Page ${pageNumber}`, pageWidth / 2, footerY, { align: "center" });
        
        const genDate = new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        const genTime = new Date().toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        doc.text(`Created on ${genDate} ${genTime}`, pageWidth - margin, footerY, { align: "right" });
    };

    const addHeader = () => {
        // Get company name from localStorage
        const getCompanyName = () => {
            try {
                // Try different possible localStorage keys for company data
                const globalSettings = localStorage.getItem('globalSettings');
                const company = localStorage.getItem('company');
                const userData = localStorage.getItem('userData');
                const userProfile = localStorage.getItem('userProfile');
                
                if (globalSettings) {
                    const settings = JSON.parse(globalSettings);
                    if (settings.companyName) return settings.companyName;
                }
                
                if (company) {
                    const companyData = JSON.parse(company);
                    if (companyData.companyName) return companyData.companyName;
                    if (companyData.name) return companyData.name;
                }
                
                if (userData) {
                    const user = JSON.parse(userData);
                    if (user.companyName) return user.companyName;
                    if (user.company && user.company.name) return user.company.name;
                }
                
                if (userProfile) {
                    const profile = JSON.parse(userProfile);
                    if (profile.companyName) return profile.companyName;
                    if (profile.company) return profile.company;
                }
                
                return 'LAW FIRM';
            } catch (error) {
                console.error('Error retrieving company name:', error);
                return 'LAW FIRM';
            }
        };

        // Extract date and count from actual data - now includes tasks
        let scheduleDate = '';
        let totalHearings = 0;
        let totalTasks = 0;
        
        // Count total hearings and tasks and try to extract date
        if (Array.isArray(dashboardData)) {
            totalHearings = dashboardData.length;
            // Try to get date from first hearing if available
            if (dashboardData.length > 0 && dashboardData[0].caseDate && dashboardData[0].caseDate.nextHearingDate) {
                try {
                    const dateStr = dashboardData[0].caseDate.nextHearingDate;
                    const cleanDateStr = dateStr.replace(/(\d+)(th|st|nd|rd)/, '$1');
                    const date = new Date(cleanDateStr);
                    if (!isNaN(date.getTime())) {
                        scheduleDate = date.toLocaleDateString('en-GB', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        });
                    }
                } catch (e) {
                    scheduleDate = 'Schedule Date';
                }
            }
        } else if (dashboardData.hearings && Array.isArray(dashboardData.hearings)) {
            totalHearings = dashboardData.hearings.length;
        } else if (typeof dashboardData === 'object') {
            Object.keys(dashboardData).forEach(date => {
                const dayData = dashboardData[date];
                if (dayData && dayData.hearings) {
                    totalHearings += dayData.hearings.length;
                }
                if (dayData && dayData.tasks) {
                    totalTasks += dayData.tasks.length;
                }
                if (Array.isArray(dayData)) {
                    totalHearings += dayData.length;
                }
            });
            
            // Try to extract a meaningful date from the first hearing
            const firstDateKey = Object.keys(dashboardData)[0];
            if (firstDateKey) {
                try {
                    const date = new Date(firstDateKey);
                    if (!isNaN(date.getTime())) {
                        scheduleDate = date.toLocaleDateString('en-GB', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        });
                    }
                } catch (e) {
                    // If date parsing fails, try to extract from hearing data
                    const hearingData = dashboardData[firstDateKey];
                    if (hearingData && hearingData.hearings && hearingData.hearings[0]) {
                        const firstHearing = hearingData.hearings[0];
                        if (firstHearing.nextHearingDate) {
                            try {
                                const hearingDate = new Date(firstHearing.nextHearingDate.replace(/(\d+)(th|st|nd|rd)/, '$1'));
                                if (!isNaN(hearingDate.getTime())) {
                                    scheduleDate = hearingDate.toLocaleDateString('en-GB', {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    });
                                }
                            } catch (dateError) {
                                scheduleDate = firstHearing.nextHearingDate || 'Schedule Date';
                            }
                        }
                    }
                }
            }
        }

        // Fallback if no date found
        if (!scheduleDate) {
            scheduleDate = new Date().toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        }

        const companyName = getCompanyName();
        const totalItems = totalHearings + totalTasks;

        // Main border for header
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(1);
        doc.rect(margin, currentY, contentWidth, 80, 'S');

        // Company name - large text on left
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(companyName, margin + 10, currentY + 30);
        
        // Total items - medium text on left, below company name
        doc.setFontSize(14);
        doc.setFont("helvetica", "normal");
        let countText = '';
        if (totalTasks > 0) {
            countText = `Total Items: ${totalItems} (${totalHearings} Hearings, ${totalTasks} Tasks)`;
        } else {
            countText = `Total Hearings: ${totalHearings}`;
        }
        doc.text(countText, margin + 10, currentY + 55);

        // Schedule date - small text on right side
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(scheduleDate, pageWidth - margin - 10, currentY + 25, { align: "right" });

        currentY += 90;
    };

    // NEW: Function to add court hearings header only when there are hearings
    const addCourtHearingsHeader = () => {
        checkNewPage(100);

        const colWidths = orientation === 'landscape' 
            ? [180, 150, 50, 260] 
            : [140, 110, 45, 145];

        // Header row with increased height
        const headerHeight = 30;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.rect(margin, currentY, contentWidth, headerHeight, 'S');
        
        let x = margin;
        for (let i = 0; i < colWidths.length - 1; i++) {
            x += colWidths[i];
            doc.line(x, currentY, x, currentY + headerHeight);
        }

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Court Name", margin + 5, currentY + 18);
        doc.text("CR No", margin + colWidths[0] + 5, currentY + 18);
        doc.text("SL No", margin + colWidths[0] + colWidths[1] + 5, currentY + 18);
        doc.text("Case Title/Case Number", margin + colWidths[0] + colWidths[1] + colWidths[2] + 5, currentY + 18);

        currentY += headerHeight + 5;
        return colWidths;
    };

    const addAllHearingsAndTasks = async () => {
        console.log('Dashboard data structure:', dashboardData);
        
        // Handle different possible data structures
        let hearingsList = [];
        let tasksList = [];
        
        // If dashboardData is an array directly
        if (Array.isArray(dashboardData)) {
            hearingsList = dashboardData;
        } 
        // If dashboardData has a hearings property
        else if (dashboardData.hearings && Array.isArray(dashboardData.hearings)) {
            hearingsList = dashboardData.hearings;
        }
        // If dashboardData is structured by dates
        else if (typeof dashboardData === 'object') {
            const datesWithData = Object.keys(dashboardData)
                .filter(date => {
                    const data = dashboardData[date];
                    return data && (data.hearings || data.tasks || Array.isArray(data));
                })
                .sort((a, b) => new Date(a) - new Date(b));

            for (const date of datesWithData) {
                const data = dashboardData[date];
                
                // Extract hearings
                if (data.hearings && Array.isArray(data.hearings)) {
                    hearingsList = hearingsList.concat(data.hearings);
                } else if (Array.isArray(data)) {
                    hearingsList = hearingsList.concat(data);
                }
                
                // Extract tasks
                if (data.tasks && Array.isArray(data.tasks)) {
                    tasksList = tasksList.concat(data.tasks);
                }
            }
        }

        console.log('Processing hearings list:', hearingsList);
        console.log('Processing tasks list:', tasksList);
        console.log('Total hearings found:', hearingsList.length);
        console.log('Total tasks found:', tasksList.length);

        // Only add court hearings section if there are hearings
        if (hearingsList.length > 0) {
            const colWidths = addCourtHearingsHeader();
            
            // Add hearings
            for (const hearing of hearingsList) {
                await addHearingRow(hearing, colWidths);
            }
        }

        // Only add tasks section if there are tasks
        if (tasksList.length > 0) {
            await addTasksSection(tasksList);
        }

        // If no data at all, show a message
        if (hearingsList.length === 0 && tasksList.length === 0) {
            checkNewPage(50);
            doc.setFontSize(14);
            doc.setFont("helvetica", "normal");
            doc.text("No hearings or tasks scheduled for the selected date(s).", margin + 10, currentY + 30);
            currentY += 50;
        }
    };

    const addTasksSection = async (tasksList) => {
        // Check if a new page is needed for the section header and the task table header
        checkNewPage(80);

        // Tasks section header with background
        doc.setDrawColor(0, 0, 0);
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, currentY, contentWidth, 25, 'FD');

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("TASKS & INTERNAL MATTERS", margin + 5, currentY + 16);
        currentY += 30;

        // Define column widths for 3-column layout (removing status column)
        const tasksColWidths = orientation === 'landscape' 
            ? [180, 150, 460]  // CNR No, Created At, Task Details (wider last column)
            : [140, 110, 195]; // CNR No, Created At, Task Details (wider last column)

        // Add a border around the task headings here
        const headerHeight = 30;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.rect(margin, currentY, contentWidth, headerHeight, 'S');

        // Add vertical lines for 3 columns only
        let x = margin;
        for (let i = 0; i < tasksColWidths.length - 1; i++) {
            x += tasksColWidths[i];
            doc.line(x, currentY, x, currentY + headerHeight);
        }
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        
        // Position the text within the new bordered box (3 columns only)
        doc.text("CNR No", margin + 5, currentY + 18);
        doc.text("Created At", margin + tasksColWidths[0] + 5, currentY + 18);
        doc.text("Task Details", margin + tasksColWidths[0] + tasksColWidths[1] + 5, currentY + 18);

        // Increment currentY to account for the new header border
        currentY += headerHeight + 5;

        // Add tasks as rows with the new column widths
        for (const task of tasksList) {
            await addTaskRowAsHearing(task, tasksColWidths);
        }
    };

    const addTaskRowAsHearing = async (task, colWidths) => {
        const assignedToNames = task.assignedTo && Array.isArray(task.assignedTo)
            ? await Promise.all(task.assignedTo.map(id => fetchUserName(id)))
            : ['Unassigned'];

        // Calculate required height based on content with larger font size
        const courtRefWrapped = wrapTextInCell(task.courtRef || 'INTERNAL MATTER', colWidths[0] - 10, 10, 4);
        const taskNameWrapped = wrapTextInCell(task.taskName || '', colWidths[2] - 10, 10, 5); // Changed to colWidths[2]
        
        const maxLines = Math.max(
            courtRefWrapped.lines.length,
            taskNameWrapped.lines.length,
            3 // Minimum for other columns
        );
        
        const rowHeight = Math.max(60, maxLines * 14 + 20);

        checkNewPage(rowHeight + 10);

        // Draw row border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.rect(margin, currentY, contentWidth, rowHeight, 'S');

        // Add vertical lines for 3 columns only
        let x = margin;
        for (let i = 0; i < colWidths.length - 1; i++) {
            x += colWidths[i];
            doc.line(x, currentY, x, currentY + rowHeight);
        }

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        // CNR No with wrapping 
        let textY = currentY + 16;
        courtRefWrapped.lines.forEach(line => {
            doc.text(line, margin + 5, textY);
            textY += 14;
        });

        // Created At (formatted date)
        const taskRef = formatDate(task.createdAt);
        doc.text(taskRef.toString(), margin + colWidths[0] + 5, currentY + 20);

        // Task Details (Task name and assignee) - now in the third column
        textY = currentY + 16;
        taskNameWrapped.lines.forEach(line => {
            doc.text(line, margin + colWidths[0] + colWidths[1] + 5, textY);
            textY += 14;
        });

        // Add assignee info
        const assigneeLine = `Assigned to: ${assignedToNames[0]}`;
        if (textY < currentY + rowHeight - 14) {
            doc.text(assigneeLine, margin + colWidths[0] + colWidths[1] + 5, textY);
        }

        // Add due date info with completion status
        const dueDate = formatDate(task.dueDate);
        const status = task.isCompleted ? "COMPLETED" : "PENDING";
        if (textY + 14 < currentY + rowHeight - 14) {
            doc.text(`Due: ${dueDate} (${status})`, margin + colWidths[0] + colWidths[1] + 5, textY + 14);
        }

        currentY += rowHeight + 3;
    };

    const addHearingRow = async (hearing, colWidths) => {
        const hearingData = extractHearingDataFromSchedule(hearing);
        
        // Calculate required height based on content with larger font size
        const courtNameWrapped = wrapTextInCell(hearingData.courtName, colWidths[0] - 10, 10, 4);
        const caseDetailsWrapped = wrapTextInCell(hearingData.caseDetails, colWidths[3] - 10, 10, 5);
        
        const maxLines = Math.max(
            courtNameWrapped.lines.length,
            caseDetailsWrapped.lines.length,
            2 // Minimum for CR No and SL No
        );
        
        const rowHeight = Math.max(60, maxLines * 14 + 20);

        checkNewPage(rowHeight + 10);

        // Draw row border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.rect(margin, currentY, contentWidth, rowHeight, 'S');

        // Add vertical lines for columns
        let x = margin;
        for (let i = 0; i < colWidths.length - 1; i++) {
            x += colWidths[i];
            doc.line(x, currentY, x, currentY + rowHeight);
        }

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        // Court Name with wrapping
        let textY = currentY + 16;
        courtNameWrapped.lines.forEach(line => {
            doc.text(line, margin + 5, textY);
            textY += 14;
        });

        // CR No
        doc.text(hearingData.crNo, margin + colWidths[0] + 5, currentY + 20);

        // SL No
        doc.text(hearingData.slNo, margin + colWidths[0] + colWidths[1] + 5, currentY + 20);

        // Case Details with wrapping
        textY = currentY + 16;
        caseDetailsWrapped.lines.forEach(line => {
            doc.text(line, margin + colWidths[0] + colWidths[1] + colWidths[2] + 5, textY);
            textY += 14;
        });

        currentY += rowHeight + 3;
    };

    const extractHearingDataFromSchedule = (hearing) => {
        // Debug: Log the actual hearing structure
        console.log('Processing hearing data:', hearing);
        
        // Handle the actual data structure from your schedule
        let courtName = 'Unknown Court';
        let crNo = 'NA';
        let slNo = 'NA';
        let caseDetails = 'Unknown Case';

        // Access the caseDate object which contains the main data
        const caseData = hearing.caseDate || hearing;

        // Extract court name from the court field and map to high court name if it's a number
        if (caseData.court) {
            // Check if court field is a number (high court ID)
            const courtId = parseInt(caseData.court);
            if (!isNaN(courtId) && highCourtMap[courtId]) {
                courtName = getHighCourtName(courtId);
            } else {
                courtName = caseData.court;
            }
        }

        if (caseData.caseTypeAndNumber) {
            crNo = caseData.caseTypeAndNumber;
        }

        // Extract serial number from serialNumber field
        if (caseData.serialNumber) {
            slNo = caseData.serialNumber;
        }

        // Build case details from available information
        let caseTitle = '';
        let caseNumber = '';

        // Get case type and number
        if (caseData.caseTypeAndNumber) {
            caseNumber = caseData.caseTypeAndNumber;
        }

        // Get parties information for case title
        if (caseData.parties) {
            caseTitle = caseData.parties;
        }

        // Combine case information
        if (caseTitle && caseNumber) {
            caseDetails = `${caseTitle} , ${caseNumber}`;
        } else if (caseNumber) {
            caseDetails = caseNumber;
        } else if (caseTitle) {
            caseDetails = caseTitle;
        } else if (hearing.courtRef) {
            caseDetails = hearing.courtRef;
        }

        const result = {
            courtName: cleanText(courtName, 'Unknown Court'),
            crNo: cleanText(crNo, 'NA'),
            slNo: cleanText(slNo, 'NA'),
            caseDetails: cleanText(caseDetails, 'Unknown Case')
        };

        console.log('Extracted data:', result);
        return result;
    };

    try {
        addHeader();
        await addAllHearingsAndTasks();
        addFooter();
        
        const filename = `Hearing_Schedule_${orientation}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);
        console.log(`PDF exported successfully in ${orientation} orientation as ${filename}!`);
    } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
    }
};